import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Inbox } from 'lucide-react';

import { adminApi } from '../services/api.jsx';
import { EtatDonnees, IndicateurActualisation, SqueletteTableau, useRequete } from '../etats';

/* ═══════════════════════════════════════════════════════════════════════════
   CANDIDATURES — la liste

   Une ligne par candidature, l'essentiel seulement : qui, pour quel profil,
   quelle ancienneté, d'où, quand, où en est le dossier. Le dossier complet —
   CV affiché, domaines, motivation, grade, décision, messages — s'ouvre sur
   sa propre page (`pages/candidature-detail.jsx`). La fiche dépliée à droite
   de la liste prenait toute la place pour n'en montrer qu'une à la fois.
   ═══════════════════════════════════════════════════════════════════════════ */

const STATUTS = [
  { cle: '', libelle: 'Toutes' },
  { cle: 'en_attente', libelle: 'En attente' },
  { cle: 'en_examen', libelle: 'En examen' },
  { cle: 'acceptee', libelle: 'Acceptées' },
  { cle: 'refusee', libelle: 'Non retenues' },
];

export const CLASSE_STATUT = {
  en_attente: 'etiq-attente',
  en_examen: 'etiq-cours',
  acceptee: 'etiq-ok',
  refusee: 'etiq-refus',
};

export function anciennete(annees) {
  if (!annees) return 'Moins d’un an';
  return `${annees} an${annees > 1 ? 's' : ''}`;
}

export default function Candidatures() {
  const navigate = useNavigate();
  const [filtre, setFiltre] = useState('en_attente');
  const charger = useCallback(async () => {
    const donnees = await adminApi.getCandidatures({ statut: filtre });
    return Array.isArray(donnees) ? donnees : [];
  }, [filtre]);
  // Changer de filtre garde la liste affichée pendant l'actualisation.
  const requete = useRequete(charger, [charger]);

  const ouvrir = (c) => navigate(`/coaches/candidatures/${c.id}`);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filtrer par statut">
        {STATUTS.map((s) => (
          <button key={s.cle || 'toutes'} type="button" role="tab" aria-selected={filtre === s.cle}
                  onClick={() => setFiltre(s.cle)}
                  className={`act act-sm ${filtre === s.cle ? 'act-fort' : 'act-doux'}`}>
            {s.libelle}
          </button>
        ))}
      </div>

      <EtatDonnees
        requete={requete}
        squelette={<SqueletteTableau colonnes={7} />}
        filtresActifs={!!filtre}
        onEffacer={filtre ? () => setFiltre('') : undefined}
        vide={{ icone: Inbox, titre: 'Aucune candidature pour l’instant',
                aide: 'Les candidatures déposées depuis « Devenir coach/mentor » arrivent ici.' }}
      >
        {(lot) => (
        <>
        <div className="flex justify-end mb-2">
          <IndicateurActualisation actif={requete.actualisation} majLe={requete.majLe} onActualiser={requete.recharger} />
        </div>
        <div className="carte-tableau">
          <div className="tableau-hote">
            <table className="tableau-cartes w-full">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  {['Candidat', 'Profil', 'Expérience', 'Localisation', 'Déposée le', 'Statut', ''].map((h) => (
                    <th key={h || 'actions'}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lot.map((c) => (
                  <tr key={c.id} onClick={() => ouvrir(c)}
                      className="border-b border-[var(--border)] hover:bg-[var(--bg-main)] transition-colors cursor-pointer">
                    <td data-libelle="Candidat" className="px-5 py-3.5">
                      <p className="text-[14px] font-semibold text-[var(--text-bold)]">{c.nom_complet}</p>
                      <p className="text-[12px] text-[var(--text-muted)] truncate max-w-[260px]">{c.email}</p>
                    </td>
                    <td data-libelle="Profil" className="px-5 py-3.5 text-[13px] text-[var(--text-main)]">{c.profil_libelle}</td>
                    <td data-libelle="Expérience" className="px-5 py-3.5 text-[13px] text-[var(--text-main)] tabular-nums">{anciennete(c.annees_experience)}</td>
                    <td data-libelle="Localisation" className="px-5 py-3.5 text-[13px] text-[var(--text-main)]">
                      {[c.ville, c.pays].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td data-libelle="Déposée le" className="px-5 py-3.5 text-[13px] text-[var(--text-muted)] tabular-nums">
                      {new Date(c.date_depot).toLocaleDateString('fr-FR')}
                    </td>
                    <td data-libelle="Statut" className="px-5 py-3.5">
                      <span className={`etiquette ${CLASSE_STATUT[c.statut] || 'etiq-neutre'}`}>{c.statut_libelle}</span>
                    </td>
                    <td data-actions="" className="px-5 py-3.5 text-right">
                      <button type="button" onClick={(e) => { e.stopPropagation(); ouvrir(c); }}
                              className="act act-icone act-sm act-contour"
                              title="Voir le dossier" aria-label={`Voir le dossier de ${c.nom_complet}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </EtatDonnees>
    </div>
  );
}
